import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useGetMyEventRegistrationQuery } from "../api/eventsApi";

/**
 * Landing page OnePay redirects to after an event registration fee is paid. It
 * polls the registration until VerifyEventPaymentJob flips it to 'paid', then
 * sends the member on to their registration (invoice + access card).
 */
export default function EventPaymentProcessingPage() {
  const { regId = "" } = useParams();
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(0);

  const { data: reg, refetch } = useGetMyEventRegistrationQuery(regId, {
    pollingInterval: 4000,
    skip: !regId,
  });

  const status = reg?.payment_status;

  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (status === "paid" || status === "free") {
      navigate(`/my-events/${regId}`, { replace: true });
    }
  }, [status, regId, navigate]);

  const slow = elapsed > 30 && status !== "paid";

  return (
    <div className="max-w-lg mx-auto p-6">
      <div className="bg-white border rounded-2xl p-8 text-center shadow-sm">
        {status === "paid" ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="text-lg font-bold text-gray-900 mt-4">Payment received</h1>
            <p className="text-sm text-gray-500 mt-1">Taking you to your registration…</p>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-indigo-500 mx-auto animate-spin" />
            <h1 className="text-lg font-bold text-gray-900 mt-4">Confirming your payment…</h1>
            <p className="text-sm text-gray-500 mt-1">
              This can take a few moments. Please keep this page open.
            </p>

            {slow ? (
              <div className="mt-5 text-left text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3 flex gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Still confirming. If you completed payment, it should clear shortly. You can{" "}
                  <button onClick={() => refetch()} className="underline font-semibold">
                    check again
                  </button>{" "}
                  or return to{" "}
                  <Link to="/my-events" className="underline font-semibold">
                    My Events
                  </Link>
                  .
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
